// context/AuthProvider.tsx
import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, Session } from '@supabase/supabase-js'
import type { AuthSessionResult, AuthSessionSource } from '../lib/supabase'
import { authHelpers, fetchProfilePlan, getAuthErrorCode, invalidateAuthSessionCache, supabase } from '../lib/supabase'
import { checkBetaAccess, isClosedBetaEnabled } from '@/core/auth/betaAccess'

interface AuthContextType {
  user: User | null
  session: Session | null
  sessionSource: AuthSessionSource | null
  sdkSessionReady: boolean
  sessionRecoveryState: 'healthy' | 'fallback' | 'expired'
  profilePlan: 'free' | 'premium'
  isPremium: boolean
  refreshProfilePlan: () => Promise<'free' | 'premium'>
  loading: boolean
  betaLoading: boolean
  isBetaAllowed: boolean
  betaReason?: string
  signUp: (email: string, password: string, redirectTo?: string) => Promise<{ error: any }>
  signIn: (email: string, password: string) => Promise<{ error: any }>
  signInWithGitHub: () => Promise<{ error: any; data: any }>
  linkWithGitHub: () => Promise<{ error: any; data: any }>
  signOut: () => Promise<{ error: any }>
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

function extractProjectRef(url?: string): string | null {
  if (!url) return null
  try {
    const host = new URL(url).hostname
    const [projectRef] = host.split('.')
    return projectRef || null
  } catch {
    return null
  }
}

function deriveSessionState(sessionResult: AuthSessionResult): {
  sessionSource: AuthSessionSource | null
  sessionRecoveryState: 'healthy' | 'fallback' | 'expired'
} {
  const errorCode = getAuthErrorCode(sessionResult.error)
  const session = sessionResult.data?.session ?? null
  if (errorCode === 'SESSION_EXPIRED') {
    return {
      sessionSource: sessionResult.source ?? null,
      sessionRecoveryState: 'expired',
    }
  }
  if (session && sessionResult.isFallback) {
    return {
      sessionSource: sessionResult.source ?? 'fallback_localstorage',
      sessionRecoveryState: 'fallback',
    }
  }
  return {
    sessionSource: session ? (sessionResult.source ?? 'supabase_sdk') : null,
    sessionRecoveryState: 'healthy',
  }
}

function dispatchSignedOutEvent() {
  try {
    window.dispatchEvent(new CustomEvent('auth:signed-out'))
  } catch {}
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [sessionSource, setSessionSource] = useState<AuthSessionSource | null>(null)
  const [sessionRecoveryState, setSessionRecoveryState] = useState<'healthy' | 'fallback' | 'expired'>('healthy')
  const [profilePlan, setProfilePlan] = useState<'free' | 'premium'>('free')
  const [loading, setLoading] = useState(true)
  const [betaLoading, setBetaLoading] = useState(false)
  const [isBetaAllowed, setIsBetaAllowed] = useState(true)
  const [betaReason, setBetaReason] = useState<string | undefined>(undefined)
  const projectRef = extractProjectRef(import.meta.env.VITE_SUPABASE_URL)
  const planRefreshSeq = React.useRef(0)
  const sdkSessionReady = sessionSource === 'supabase_sdk' && sessionRecoveryState === 'healthy' && Boolean(session)

  const refreshProfilePlanFor = React.useCallback(async (
    targetUser: Pick<User, 'id' | 'email'> | null | undefined,
    targetSession?: Pick<Session, 'access_token'> | null,
  ): Promise<'free' | 'premium'> => {
    const requestId = ++planRefreshSeq.current
    if (!targetUser?.id) {
      if (requestId === planRefreshSeq.current) {
        setProfilePlan('free')
      }
      return 'free'
    }

    try {
      const accessToken = targetSession?.access_token
      const result = await fetchProfilePlan({
        userId: targetUser.id,
        accessToken,
      })
      if (import.meta.env.DEV) {
        console.debug('[auth] plan source: profiles', {
          projectRef,
          userId: targetUser.id,
          userEmail: targetUser.email ?? null,
          sessionSource: targetSession?.access_token ? 'resolved_session_token' : null,
          sdkSessionReady: Boolean(targetSession?.access_token),
          profileExists: result.profileExists,
          rawPlan: result.rawPlan,
          resolvedPlan: result.resolvedPlan,
        })
      }
      if (!result.profileExists) {
        console.warn('[auth] usuario autenticado sin fila en profiles; se aplica plan free por seguridad', {
          projectRef,
          userId: targetUser.id,
        })
      } else if (result.rawPlan !== 'free' && result.rawPlan !== 'premium' && result.rawPlan != null) {
        console.warn('[auth] valor de plan inesperado en profiles; se aplica free por seguridad', {
          projectRef,
          userId: targetUser.id,
          plan: result.rawPlan,
        })
      }
      if (requestId === planRefreshSeq.current) {
        setProfilePlan(result.resolvedPlan)
      }
      return result.resolvedPlan
    } catch (error) {
      console.debug('[auth] plan fallback a free:', (error as Error)?.message)
      if (requestId === planRefreshSeq.current) {
        setProfilePlan('free')
      }
      return 'free'
    }
  }, [projectRef])

  const refreshProfilePlan = React.useCallback(async (): Promise<'free' | 'premium'> => {
    return await refreshProfilePlanFor(user, session)
  }, [refreshProfilePlanFor, session, user])

  useEffect(() => {
    let mounted = true
    let subscription: any = null

    const resolveBetaAccess = async (nextUser: User | null) => {
      if (!isClosedBetaEnabled()) {
        if (mounted) {
          setIsBetaAllowed(true)
          setBetaReason(undefined)
          setBetaLoading(false)
        }
        return
      }

      if (!nextUser?.email) {
        if (mounted) {
          setIsBetaAllowed(false)
          setBetaReason('Cuenta sin email. Algunas funciones con acceso progresivo requieren un email validado.')
          setBetaLoading(false)
        }
        return
      }

      if (mounted) setBetaLoading(true)
      const access = await checkBetaAccess(nextUser.email)
      if (!mounted) return
      setIsBetaAllowed(access.allowed)
      setBetaReason(access.allowed ? undefined : access.reason || 'Acceso no disponible para esta cuenta en la beta pública.')
      setBetaLoading(false)
    }

    const applySessionResult = async (
      origin: 'init' | 'visibilitychange',
      sessionResult: AuthSessionResult,
    ) => {
      const nextSession = sessionResult.data?.session ?? null
      const nextUser = nextSession?.user ?? null
      const nextState = deriveSessionState(sessionResult)

      if (nextState.sessionRecoveryState === 'expired') {
        console.warn(`[auth] ${origin}: sesión caducada, requiere re-login`)
      } else if (nextState.sessionRecoveryState === 'fallback') {
        console.warn(`[auth] ${origin}: sesión fallback activa, UI sincronizada sin reinyectar al SDK`)
      } else if (origin === 'visibilitychange' && nextSession) {
        console.log('[auth] visibilitychange: sesión SDK válida confirmada')
      }

      if (!mounted) return
      setSession(nextSession)
      setUser(nextUser)
      setSessionSource(nextState.sessionSource)
      setSessionRecoveryState(nextState.sessionRecoveryState)
      await refreshProfilePlanFor(nextUser, nextSession)
      await resolveBetaAccess(nextUser)
      setLoading(false)
    }

    const initAuth = async () => {
      try {
        const sessionResult = await authHelpers.getSession()
        if (sessionResult.isFallback && !sessionResult.error) {
          console.warn('[auth] session initialized via fallback_localstorage')
        }
        if (sessionResult.error) {
          console.warn('Auth getSession warning:', sessionResult.error.message)
        }
        await applySessionResult('init', sessionResult)
      } catch (error) {
        console.warn('Auth initialization failed:', error)
        if (mounted) {
          setSession(null)
          setUser(null)
          setSessionSource(null)
          setSessionRecoveryState('expired')
          setIsBetaAllowed(!isClosedBetaEnabled())
          setBetaReason(undefined)
          setBetaLoading(false)
          setLoading(false)
        }
      }
    }

    const setupAuthListener = () => {
      try {
        const { data: { subscription: authSubscription } } = authHelpers.onAuthStateChange(
          async (_event: any, nextSession: Session | null) => {
            if (mounted) {
              setSession(nextSession)
              setUser(nextSession?.user ?? null)
              setSessionSource(nextSession ? 'supabase_sdk' : null)
              setSessionRecoveryState('healthy')
              await refreshProfilePlanFor(nextSession?.user ?? null, nextSession)
              await resolveBetaAccess(nextSession?.user ?? null)
              setLoading(false)
              try {
                if (nextSession && supabase) {
                  await supabase.rpc('ensure_beta_profile')
                }
              } catch (e) {
                console.debug('ensure_beta_profile skipped:', (e as any)?.message)
              }
            }
          }
        )
        subscription = authSubscription
      } catch (error) {
        console.warn('Auth listener setup failed:', error)
      }
    }

    initAuth()
    setupAuthListener()

    return () => {
      mounted = false
      if (subscription?.unsubscribe) {
        try {
          subscription.unsubscribe()
        } catch (error) {
          console.warn('Error unsubscribing from auth changes:', error)
        }
      }
    }
  }, [refreshProfilePlanFor])

  useEffect(() => {
    if (!user) return
    let active = true

    const resolveBetaAccess = async (nextUser: User | null) => {
      if (!isClosedBetaEnabled()) {
        if (active) {
          setIsBetaAllowed(true)
          setBetaReason(undefined)
          setBetaLoading(false)
        }
        return
      }

      if (!nextUser?.email) {
        if (active) {
          setIsBetaAllowed(false)
          setBetaReason('Cuenta sin email. Algunas funciones con acceso progresivo requieren un email validado.')
          setBetaLoading(false)
        }
        return
      }

      if (active) setBetaLoading(true)
      const access = await checkBetaAccess(nextUser.email)
      if (!active) return
      setIsBetaAllowed(access.allowed)
      setBetaReason(access.allowed ? undefined : access.reason || 'Acceso no disponible para esta cuenta en la beta pública.')
      setBetaLoading(false)
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      void authHelpers.getSession()
        .then(async (sessionResult) => {
          const nextSession = sessionResult.data?.session ?? null
          const nextUser = nextSession?.user ?? null
          const nextState = deriveSessionState(sessionResult)

          if (nextState.sessionRecoveryState === 'expired') {
            console.warn('[auth] visibilitychange: sesión caducada, requiere re-login')
          } else if (nextState.sessionRecoveryState === 'fallback') {
            console.warn('[auth] visibilitychange: sesión fallback activa, UI sincronizada sin reinyectar al SDK')
          } else if (nextSession) {
            console.log('[auth] visibilitychange: sesión SDK válida confirmada')
          }

          if (!active) return
          setSession(nextSession)
          setUser(nextUser)
          setSessionSource(nextState.sessionSource)
          setSessionRecoveryState(nextState.sessionRecoveryState)
          await resolveBetaAccess(nextUser)
          if (nextUser?.id) {
            await refreshProfilePlanFor(nextUser, nextSession)
          } else {
            setProfilePlan('free')
          }
          setLoading(false)
        })
        .catch((error) => {
          console.warn('[auth] visibilitychange: session check failed (non-fatal):', error)
        })
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      active = false
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [refreshProfilePlanFor, user])

  useEffect(() => {
    void refreshProfilePlanFor(user, session)
  }, [refreshProfilePlanFor, session, user])

  const signUp = async (email: string, password: string, redirectTo?: string) => {
    setLoading(true)
    const result = await authHelpers.signUp(email, password, redirectTo)
    setLoading(false)
    return result
  }

  const signIn = async (email: string, password: string) => {
    setLoading(true)
    const result = await authHelpers.signIn(email, password)
    setLoading(false)
    return result
  }

  const signInWithGitHub = async () => {
    setLoading(true)
    const result = await authHelpers.signInWithGitHub()
    setLoading(false)
    return result
  }

  const linkWithGitHub = async () => {
    setLoading(true)
    const result = await authHelpers.linkWithGitHub()
    setLoading(false)
    return result
  }

  const signOut = async () => {
    setLoading(true)
    const result = await authHelpers.signOut({ mode: 'hard' })
    invalidateAuthSessionCache()

    let postLogoutSession: Session | null = null
    try {
      const freshSession = await authHelpers.getSession({ forceFresh: true })
      postLogoutSession = freshSession.data?.session ?? null
    } catch {
      postLogoutSession = null
    }

    if (!postLogoutSession) {
      setSession(null)
      setUser(null)
      setSessionSource(null)
      setSessionRecoveryState('healthy')
      setProfilePlan('free')
      setIsBetaAllowed(!isClosedBetaEnabled())
      setBetaReason(undefined)
      setBetaLoading(false)
      dispatchSignedOutEvent()
    }

    setLoading(false)
    return result
  }

  const value: AuthContextType = {
    user,
    session,
    sessionSource,
    sdkSessionReady,
    sessionRecoveryState,
    profilePlan,
    isPremium: profilePlan === 'premium',
    refreshProfilePlan,
    loading,
    betaLoading,
    isBetaAllowed,
    betaReason,
    signUp,
    signIn,
    signInWithGitHub,
    linkWithGitHub,
    signOut,
    isAuthenticated: !!user,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
